import React, { useState } from 'react';
import { CreditCard, TrendingUp, Calendar, Download, DollarSign, Clock, Cpu, HardDrive, AlertCircle, Info, Filter, Search, Sparkles, BarChart3, Wallet, Receipt } from 'lucide-react';
import toast from 'react-hot-toast';
import { BillRecord } from '../../../types/api';

interface BillingSummary {
  totalAmount: number;
  thisMonth: number;
  lastMonth: number;
  balance: number;
  credits: number;
}

interface JobCostDetail {
  jobId: string;
  jobName: string;
  startTime: string;
  endTime: string;
  duration: string;
  nodes: number;
  cores: number;
  nodeType: string;
  computeCost: number;
  storageCost: number;
  networkCost: number;
  totalCost: number;
}

const BillingCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('thisMonth');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // 账单汇总数据
  const billingSummary: BillingSummary = {
    totalAmount: 2847.50,
    thisMonth: 485.30,
    lastMonth: 672.80,
    balance: 1250.00,
    credits: 200.00
  };

  // 计费记录
  const billingRecords: BillRecord[] = [
    {
      id: 'bill_001',
      date: '2024-01-15 14:30:00',
      type: 'compute',
      description: 'GPU计算节点 - GROMACS分子动力学',
      amount: -45.60,
      duration: '6小时',
      resource: 'g-v100-1',
      jobId: '22'
    },
    {
      id: 'bill_002',
      date: '2024-01-15 09:15:00',
      type: 'storage',
      description: '文件存储 - CPFS-G高性能存储',
      amount: -12.30,
      duration: '24小时',
      resource: '200GB'
    },
    {
      id: 'bill_003',
      date: '2024-01-14 16:45:00',
      type: 'compute',
      description: 'CPU计算节点 - TensorFlow训练',
      amount: -28.90,
      duration: '8小时',
      resource: 'c-16-4',
      jobId: '23'
    },
    {
      id: 'bill_004',
      date: '2024-01-14 10:00:00',
      type: 'recharge',
      description: '账户充值',
      amount: 500.00
    },
    {
      id: 'bill_005',
      date: '2024-01-13 11:20:00',
      type: 'network',
      description: '数据传输费用',
      amount: -8.75,
      resource: '50GB'
    }
  ];

  // 作业费用详情
  const jobCostDetails: JobCostDetail[] = [
    {
      jobId: '22',
      jobName: '分子动力学模拟-蛋白质折叠',
      startTime: '2024-01-15 09:32:00',
      endTime: '2024-01-15 15:32:00',
      duration: '6小时',
      nodes: 1,
      cores: 8,
      nodeType: 'g-v100-1',
      computeCost: 43.20,
      storageCost: 2.40,
      networkCost: 0.00,
      totalCost: 45.60
    },
    {
      jobId: '23',
      jobName: 'CNN模型训练-图像识别',
      startTime: '2024-01-14 08:17:00',
      endTime: '2024-01-14 16:17:00',
      duration: '8小时',
      nodes: 2,
      cores: 32,
      nodeType: 'c-16-4',
      computeCost: 26.40,
      storageCost: 1.80,
      networkCost: 0.70,
      totalCost: 28.90
    },
    {
      jobId: '24',
      jobName: '流体力学仿真-湍流分析',
      startTime: '2024-01-13 10:20:00',
      endTime: '2024-01-13 22:20:00',
      duration: '12小时',
      nodes: 4,
      cores: 64,
      nodeType: 'c-16-4',
      computeCost: 76.80,
      storageCost: 4.20,
      networkCost: 1.50,
      totalCost: 82.50
    }
  ];

  // 资源价格表
  const resourcePricing = [
    { resource: 'c-4-1', type: 'CPU节点', price: '¥0.80/核·小时', description: '4核1G，通用计算' },
    { resource: 'c-8-2', type: 'CPU节点', price: '¥0.85/核·小时', description: '8核2G，内存优化' },
    { resource: 'c-16-4', type: 'CPU节点', price: '¥0.90/核·小时', description: '16核4G，高性能计算' },
    { resource: 'g-v100-1', type: 'GPU节点', price: '¥7.20/卡·小时', description: 'NVIDIA V100，深度学习' },
    { resource: 'g-a100-1', type: 'GPU节点', price: '¥12.80/卡·小时', description: 'NVIDIA A100，AI训练' },
    { resource: 'h-c-8-2', type: '高频CPU', price: '¥1.20/核·小时', description: '高主频，计算密集型' },
    { resource: 'storage-standard', type: '标准存储', price: '¥0.12/GB·天', description: 'CPFS标准存储' },
    { resource: 'storage-advanced', type: '高级存储', price: '¥0.18/GB·天', description: 'CPFS高性能存储' },
    { resource: 'network', type: '网络传输', price: '¥0.15/GB', description: '公网数据传输' }
  ];

  // 充值
  const handleRecharge = (amount: number) => {
    toast.success(`充值 ¥${amount} 成功！`);
  };

  // 下载账单
  const downloadBill = () => {
    toast.success('账单下载中...');
  };

  // 过滤记录
  const filteredRecords = billingRecords.filter(record => {
    if (filterType !== 'all' && record.type !== filterType) return false;
    if (searchTerm && !record.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // 获取类型图标
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'compute': return <Cpu className="w-4 h-4" />;
      case 'storage': return <HardDrive className="w-4 h-4" />;
      case 'network': return <TrendingUp className="w-4 h-4" />;
      case 'recharge': return <CreditCard className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  // 获取类型颜色
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'compute': return 'text-blue-600 bg-blue-100';
      case 'storage': return 'text-green-600 bg-green-100';
      case 'network': return 'text-purple-600 bg-purple-100';
      case 'recharge': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const tabs = [
    { id: 'overview', label: '账单总览', icon: BarChart3 },
    { id: 'records', label: '消费明细', icon: Receipt },
    { id: 'jobs', label: '作业费用', icon: Cpu },
    { id: 'pricing', label: '价格查询', icon: DollarSign }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6 portrait:p-4">
      <div className="max-w-7xl mx-auto space-y-8 portrait:space-y-5">
        {/* 页面标题 */}
        <div className="flex items-center justify-between portrait:flex-col portrait:items-start portrait:space-y-4">
          <div className="flex items-center space-x-5 portrait:space-x-4">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 portrait:w-12 portrait:h-12 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <Wallet className="w-7 h-7 portrait:w-6 portrait:h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 portrait:w-4 portrait:h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-pulse">
                <DollarSign className="w-2.5 h-2.5 portrait:w-2 portrait:h-2 text-white" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl portrait:text-xl portrait:md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                费用中心
              </h1>
              <p className="text-gray-600 mt-2 portrait:mt-1 text-base portrait:text-sm leading-relaxed">管理账户费用、查看消费明细和价格信息</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 portrait:space-x-2 portrait:w-full portrait:justify-start">
            <button
              onClick={downloadBill}
              className="group flex items-center space-x-2 portrait:space-x-1.5 px-6 py-3 portrait:px-4 portrait:py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl portrait:rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 portrait:hover:translate-y-0 text-base portrait:text-sm"
            >
              <Download className="w-4 h-4 portrait:w-3.5 portrait:h-3.5 group-hover:rotate-12 transition-transform duration-300" />
              <span className="font-medium">下载账单</span>
            </button>
          </div>
        </div>

        {/* 导航标签 */}
        <div className="backdrop-blur-sm bg-white/70 rounded-2xl portrait:rounded-xl shadow-lg border border-white/20">
          <div className="border-b border-gray-200/50">
            <nav className="-mb-px flex portrait:overflow-x-auto portrait:scrollbar-hide">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`group flex items-center space-x-2 portrait:space-x-1.5 py-4 portrait:py-3 px-6 portrait:px-4 border-b-2 font-medium text-sm portrait:text-xs transition-all duration-300 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50/50'
                    }`}
                  >
                    <Icon className={`w-4 h-4 portrait:w-3.5 portrait:h-3.5 transition-transform duration-300 ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-105'}`} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-8 portrait:p-4">
            {/* 账单总览 */}
            {activeTab === 'overview' && (
              <div className="space-y-8 portrait:space-y-5">
                {/* 余额和充值 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 portrait:grid-cols-1 gap-6 portrait:gap-4">
                  <div className="group relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 rounded-2xl portrait:rounded-xl p-6 portrait:p-4 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 portrait:hover:translate-y-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 mb-1 portrait:mb-0.5 text-sm portrait:text-xs">账户余额</p>
                        <p className="text-3xl portrait:text-2xl font-bold">¥{billingSummary.balance.toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 portrait:w-10 portrait:h-10 bg-white/20 rounded-xl portrait:rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <Wallet className="w-6 h-6 portrait:w-5 portrait:h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="group backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl portrait:rounded-xl p-6 portrait:p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 portrait:hover:translate-y-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 mb-1 portrait:mb-0.5 text-sm portrait:text-xs">代金券余额</p>
                        <p className="text-2xl portrait:text-xl font-bold text-gray-900">¥{billingSummary.credits.toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 portrait:w-10 portrait:h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl portrait:rounded-lg flex items-center justify-center shadow-lg">
                        <Sparkles className="w-6 h-6 portrait:w-5 portrait:h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="group backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl portrait:rounded-xl p-6 portrait:p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 portrait:hover:translate-y-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 mb-1 portrait:mb-0.5 text-sm portrait:text-xs">本月消费</p>
                        <p className="text-2xl portrait:text-xl font-bold text-gray-900">¥{billingSummary.thisMonth.toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 portrait:w-10 portrait:h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-xl portrait:rounded-lg flex items-center justify-center shadow-lg">
                        <TrendingUp className="w-6 h-6 portrait:w-5 portrait:h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="group backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl portrait:rounded-xl p-6 portrait:p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 portrait:hover:translate-y-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 mb-1 portrait:mb-0.5 text-sm portrait:text-xs">上月消费</p>
                        <p className="text-2xl portrait:text-xl font-bold text-gray-900">¥{billingSummary.lastMonth.toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 portrait:w-10 portrait:h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl portrait:rounded-lg flex items-center justify-center shadow-lg">
                        <Calendar className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 快速充值 */}
                <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">快速充值</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[100, 500, 1000, 2000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => handleRecharge(amount)}
                        className="group relative overflow-hidden p-6 border-2 border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 text-center transform hover:-translate-y-1 hover:shadow-lg"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative">
                          <div className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">¥{amount}</div>
                          <div className="text-sm text-gray-500 mt-1">立即充值</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 消费趋势图表占位 */}
                <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">消费趋势</h3>
                  </div>
                  <div className="h-64 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TrendingUp className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-lg font-medium mb-2">消费趋势图表</p>
                      <p className="text-sm">功能开发中，敬请期待</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 消费明细 */}
            {activeTab === 'records' && (
              <div className="space-y-6">
                {/* 过滤器 */}
                <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                        <Filter className="w-4 h-4 text-white" />
                      </div>
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      >
                        <option value="all">全部类型</option>
                        <option value="compute">计算费用</option>
                        <option value="storage">存储费用</option>
                        <option value="network">网络费用</option>
                        <option value="recharge">充值记录</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
                        <Search className="w-4 h-4 text-white" />
                      </div>
                      <input
                        type="text"
                        placeholder="搜索描述..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="px-4 py-2 border border-gray-200 rounded-xl text-sm w-64 bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      />
                    </div>
                  </div>
                </div>

                {/* 明细列表 */}
                <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl overflow-hidden shadow-lg">
                  <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full table-fixed min-w-[600px] lg:min-w-[1000px]">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="w-[140px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            类型
                          </th>
                          <th className="w-[300px] px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            描述
                          </th>
                          <th className="w-[180px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            时间
                          </th>
                          <th className="w-[200px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            资源
                          </th>
                          <th className="w-[120px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            金额
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200/50">
                        {filteredRecords.map((record, index) => (
                          <tr key={record.id} className="hover:bg-white/50 transition-colors duration-200" style={{animationDelay: `${index * 50}ms`}}>
                            <td className="px-6 py-4 text-center">
                              <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(record.type)}`}>
                                {getTypeIcon(record.type)}
                                <span className="capitalize">{record.type}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900 truncate">{record.description}</div>
                              {record.duration && (
                                <div className="text-xs text-gray-500 mt-1">持续时间: {record.duration}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-700 font-medium">
                              {record.date}
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-700 font-medium">
                              {record.resource || '-'}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`text-sm font-semibold ${record.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {record.amount > 0 ? '+' : ''}¥{Math.abs(record.amount).toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* 作业费用详情 */}
            {activeTab === 'jobs' && (
              <div className="space-y-6">
                <div className="backdrop-blur-sm bg-yellow-50/80 border border-yellow-200/50 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Info className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold mb-2">作业费用说明</p>
                      <ul className="space-y-1 text-sm">
                        <li>• 费用基于节点而非作业进行计费</li>
                        <li>• 多作业复用同一节点时，费用将分摊到各个作业</li>
                        <li>• 费用估算仅供参考，实际计费以账单明细为准</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl overflow-hidden shadow-lg">
                  <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full table-fixed min-w-[800px] lg:min-w-[1200px]">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="w-[250px] px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            作业信息
                          </th>
                          <th className="w-[220px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            运行时间
                          </th>
                          <th className="w-[200px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            资源配置
                          </th>
                          <th className="w-[250px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            费用明细
                          </th>
                          <th className="w-[120px] px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            总费用
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200/50">
                        {jobCostDetails.map((job, index) => (
                          <tr key={job.jobId} className="hover:bg-white/50 transition-colors duration-200" style={{animationDelay: `${index * 50}ms`}}>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{job.jobName}</div>
                              <div className="text-xs text-gray-500 mt-1">作业ID: {job.jobId}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-sm font-medium text-gray-900">{job.duration}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {job.startTime} - {job.endTime}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-sm font-medium text-gray-900">{job.nodeType}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {job.nodes}节点 · {job.cores}核心
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span>计算:</span>
                                  <span className="font-medium">¥{job.computeCost.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>存储:</span>
                                  <span className="font-medium">¥{job.storageCost.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>网络:</span>
                                  <span className="font-medium">¥{job.networkCost.toFixed(2)}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-sm font-bold text-gray-900">
                                ¥{job.totalCost.toFixed(2)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* 价格查询 */}
            {activeTab === 'pricing' && (
              <div className="space-y-6">
                <div className="backdrop-blur-sm bg-blue-50/80 border border-blue-200/50 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Info className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-2">计费说明</p>
                      <ul className="space-y-1 text-sm">
                        <li>• 计算资源按实际使用时长计费，精确到秒</li>
                        <li>• 存储资源按日计费，不足一天按一天计算</li>
                        <li>• 网络传输费用仅计算公网出流量</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="grid gap-6">
                  {['CPU节点', 'GPU节点', '高频CPU', '存储', '网络'].map((category, categoryIndex) => (
                    <div key={category} className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg" style={{animationDelay: `${categoryIndex * 100}ms`}}>
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">{category}</h3>
                      </div>
                      <div className="grid gap-4">
                        {resourcePricing
                          .filter(item => {
                            if (category === 'CPU节点') return item.resource.startsWith('c-');
                            if (category === 'GPU节点') return item.resource.startsWith('g-');
                            if (category === '高频CPU') return item.resource.startsWith('h-');
                            if (category === '存储') return item.resource.includes('storage');
                            if (category === '网络') return item.resource === 'network';
                            return false;
                          })
                          .map((item, index) => (
                            <div key={item.resource} className="group flex items-center justify-between p-4 bg-gradient-to-r from-gray-50/80 to-white/80 rounded-xl border border-gray-200/50 hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5" style={{animationDelay: `${(categoryIndex * 100) + (index * 50)}ms`}}>
                              <div>
                                <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">{item.type}</div>
                                <div className="text-sm text-gray-600 mt-1">{item.description}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{item.price}</div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingCenter;
